import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { formatToTimeZone, getLocalTimezone, getTZAbbr } from '@modernloop/shared/datetime';

import { ActivityLogCandidateRsvpAccepted_ActivityLogFragment, ResponseStatus } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import ActivityLogContent from 'src/entities/ActivityLog/Helpers/ActivityLogContent';

interface Fragments {
  activityLog: ActivityLogCandidateRsvpAccepted_ActivityLogFragment;
}

const ActivityLogTaskCandidateRsvpAccepted: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { details } = activityLog;

  // eslint-disable-next-line no-underscore-dangle
  if (!details || details.__typename !== 'ActivityLogCandidateRsvpAcceptedDetails') {
    throw new Error('ActivityLogCandidateRsvpAccepted: details is not ActivityLogCandidateRsvpAcceptedDetails');
  }

  const { startAt, endAt, comment } = details;
  const actionLabel = useMemo(() => {
    const timezone = getLocalTimezone();

    const startDate = formatToTimeZone(startAt, 'MMM d, yyyy', timezone);
    const startTime = formatToTimeZone(startAt, 'h:mm a', timezone);
    const endTime = formatToTimeZone(endAt, 'h:mm a', timezone);

    const timezoneAbbr = getTZAbbr(timezone);

    return {
      key: 'hiring-dashboard-candidates:candidates.activityLog.acceptedInterviewOn',
      fallback: `accepted their interview on ${startDate} from ${startTime} to ${endTime} ${timezoneAbbr}`,
      values: {
        startDate,
        startTime,
        endTime,
        timezone: timezoneAbbr,
      },
    };
  }, [endAt, startAt]);

  return (
    <ActivityLogBase
      activityLog={activityLog}
      responseStatus={ResponseStatus.Accepted}
      actionLabel={actionLabel.fallback}
      actionLabelKey={actionLabel.key}
      actionLabelValues={actionLabel.values}
    >
      {comment ? <ActivityLogContent content={comment} /> : null}
    </ActivityLogBase>
  );
};

ActivityLogTaskCandidateRsvpAccepted.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogCandidateRsvpAccepted_activityLog on ActivityLog {
      id
      details {
        __typename
        ... on ActivityLogCandidateRsvpAcceptedDetails {
          __typename
          startAt
          endAt
          comment
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogTaskCandidateRsvpAccepted;
